import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ChevronRight, Home, Maximize, BedDouble, Layers, CheckCircle, Phone, Mail } from 'lucide-react';
import { submitLead } from '../services/hubspot';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ImmobilienDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [formStatus, setFormStatus] = useState(null);

  useEffect(() => {
    const loadProperty = async () => {
      try {
        const response = await axios.get(`${API}/api/properties/${id}`);
        setProperty(response.data);
      } catch (error) {
        console.error('Fehler beim Laden:', error);
      } finally {
        setLoading(false);
      }
    };
    loadProperty();
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    const result = await submitLead('expose', {
      name:          formData.name,
      email:         formData.email,
      phone:         formData.phone,
      message:       formData.message,
      propertyTitle: property?.title,
      propertyId:    property?.id,
      propertyType:  property?.property_type,
    });
    setFormLoading(false);
    setFormStatus(result.success ? 'success' : 'error');
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-screen bg-[#131d30]">
      <div className="spinner" />
    </div>
  );

  if (!property) return (
    <div className="flex justify-center items-center min-h-screen bg-[#131d30]">
      <p className="text-[#c8c6c4]">Immobilie nicht gefunden.</p>
    </div>
  );

  return (
    <div className="bg-[#131d30] min-h-screen">
      {/* Hero / Bildergalerie */}
      <section className="relative pt-24 pb-0 bg-[#0a1120]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm mb-6 pt-8">
            <Link to="/" className="text-[#c8c6c4] hover:text-[#d1af6a]">Home</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <Link to="/immobilien" className="text-[#c8c6c4] hover:text-[#d1af6a]">Immobilien</Link>
            <ChevronRight className="w-4 h-4 text-[#c8c6c4]" />
            <span className="text-[#d1af6a]">{property.title}</span>
          </nav>

          {/* Hauptbild */}
          {property.images?.length > 0 && (
            <div className="space-y-3">
              <div className="w-full h-[500px] overflow-hidden rounded-t-lg">
                <img
                  src={property.images[activeImage]}
                  alt={property.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {property.images.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {property.images.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImage(i)}
                      className={`flex-shrink-0 w-24 h-16 overflow-hidden rounded ${
                        i === activeImage ? 'ring-2 ring-[#d1af6a]' : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt={`Bild ${i+1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">

            {/* Links: Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Titel & Status */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[#d1af6a] text-xs font-accent tracking-widest uppercase">
                    {property.property_type} · {property.transaction_type}
                  </span>
                  <span className={`px-2 py-1 text-xs ${
                    property.status === 'Neu' ? 'badge-neu' :
                    property.status === 'Verfügbar' ? 'badge-verfugbar' :
                    'badge-verhandlung'
                  }`}>
                    {property.status}
                  </span>
                </div>
                <h1 className="font-display text-3xl sm:text-4xl text-white mb-4">
                  {property.title}
                </h1>
                <p className="text-[#c8c6c4] text-lg">
                  {property.zip_code} {property.location}
                  {property.floor && ` · ${property.floor}`}
                </p>
              </motion.div>

              {/* Preis */}
              <div className="border-l-2 border-[#d1af6a] pl-6 py-2">
                <p className="text-[#c8c6c4] text-sm mb-1">Kaufpreis / Miete</p>
                <p className="font-display text-3xl text-[#d1af6a]">
                  {property.price_on_request
                    ? 'Preis auf Anfrage'
                    : property.price
                    ? `€ ${property.price.toLocaleString('de-DE')}`
                    : 'Preis auf Anfrage'}
                </p>
              </div>

              {/* Eckdaten */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-[#1a2640] border border-[#d1af6a]/20 p-4 rounded text-center">
                  <Maximize className="w-5 h-5 text-[#d1af6a] mx-auto mb-2" />
                  <p className="text-white font-semibold">{property.area_sqm} m²</p>
                  <p className="text-[#c8c6c4] text-xs">Wohnfläche</p>
                </div>
                {property.rooms && (
                  <div className="bg-[#1a2640] border border-[#d1af6a]/20 p-4 rounded text-center">
                    <BedDouble className="w-5 h-5 text-[#d1af6a] mx-auto mb-2" />
                    <p className="text-white font-semibold">{property.rooms}</p>
                    <p className="text-[#c8c6c4] text-xs">Zimmer</p>
                  </div>
                )}
                {property.floor && (
                  <div className="bg-[#1a2640] border border-[#d1af6a]/20 p-4 rounded text-center">
                    <Layers className="w-5 h-5 text-[#d1af6a] mx-auto mb-2" />
                    <p className="text-white font-semibold">{property.floor}</p>
                    <p className="text-[#c8c6c4] text-xs">Etage</p>
                  </div>
                )}
                <div className="bg-[#1a2640] border border-[#d1af6a]/20 p-4 rounded text-center">
                  <Home className="w-5 h-5 text-[#d1af6a] mx-auto mb-2" />
                  <p className="text-white font-semibold">{property.property_type}</p>
                  <p className="text-[#c8c6c4] text-xs">Objektart</p>
                </div>
              </div>

              {/* Beschreibung */}
              <div>
                <h2 className="font-display text-2xl text-white mb-4">Beschreibung</h2>
                <p className="text-[#c8c6c4] leading-relaxed whitespace-pre-line">
                  {property.description}
                </p>
              </div>

              {/* Features */}
              {property.features?.length > 0 && (
                <div>
                  <h2 className="font-display text-2xl text-white mb-4">Ausstattung</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {property.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-[#c8c6c4]">
                        <CheckCircle className="w-4 h-4 text-[#d1af6a] flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Rechts: Exposé-Formular */}
            <div className="lg:col-span-1">
              <div className="bg-[#1a2640] border border-[#d1af6a]/20 p-6 rounded sticky top-24">
                <h3 className="font-display text-xl text-white mb-2">Exposé anfordern</h3>
                <p className="text-[#c8c6c4] text-sm mb-6">
                  Kostenlos & unverbindlich – Serdar Dogan meldet sich persönlich bei Ihnen.
                </p>

                {formStatus === 'success' ? (
                  <div className="bg-green-500/10 border border-green-500/30 p-4 rounded">
                    <p className="text-green-400 font-medium">✅ Vielen Dank!</p>
                    <p className="text-green-400/80 text-sm mt-1">
                      Ihre Exposé-Anfrage wurde erfolgreich gesendet. Serdar Dogan meldet sich in Kürze bei Ihnen.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                      type="text"
                      placeholder="Ihr Name *"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                      className="input-luxury w-full"
                    />
                    <input
                      type="email"
                      placeholder="E-Mail *"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                      className="input-luxury w-full"
                    />
                    <input
                      type="tel"
                      placeholder="Telefon"
                      value={formData.phone}
                      onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                      className="input-luxury w-full"
                    />
                    <textarea
                      placeholder="Nachricht (optional)"
                      rows={3}
                      value={formData.message}
                      onChange={(e) => setFormData(p => ({ ...p, message: e.target.value }))}
                      className="input-luxury w-full resize-none"
                    />

                    {/* Datenschutz Checkbox */}
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" required className="mt-1 accent-[#d1af6a]" />
                      <span className="text-[#c8c6c4] text-xs">
                        Ich habe die{' '}
                        <Link to="/datenschutz" target="_blank" className="text-[#d1af6a] hover:underline">
                          Datenschutzerklärung
                        </Link>{' '}
                        und{' '}
                        <Link to="/agb" target="_blank" className="text-[#d1af6a] hover:underline">
                          AGB
                        </Link>{' '}
                        gelesen und bin damit einverstanden. *
                      </span>
                    </label>

                    {formStatus === 'error' && (
                      <div className="bg-red-500/10 border border-red-500/30 p-3 rounded">
                        <p className="text-red-400 text-sm">
                          Fehler beim Senden. Bitte rufen Sie uns direkt an.
                        </p>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={formLoading}
                      className="btn-primary w-full disabled:opacity-50"
                    >
                      {formLoading ? 'Wird gesendet…' : 'Exposé anfordern'}
                    </button>

                    <p className="text-[#c8c6c4]/60 text-xs text-center">
                      Ihre Daten werden vertraulich behandelt.
                    </p>
                  </form>
                )}

                {/* Direktkontakt */}
                <div className="mt-6 pt-6 border-t border-[#d1af6a]/20 space-y-3">
                  <p className="text-[#c8c6c4] text-xs uppercase tracking-wider">Direktkontakt</p>
                  <a href="tel:+49 175 6900374" className="flex items-center gap-2 text-[#d1af6a] hover:underline text-sm">
                    <Phone className="w-4 h-4" /> +49 175 6900374
                  </a>
                  <a href="mailto:serdar.dogan@immobilien-dogan.com" className="flex items-center gap-2 text-[#d1af6a] hover:underline text-sm">
                    <Mail className="w-4 h-4" /> serdar.dogan@immobilien-dogan.com
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Zurück Button */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/immobilien" className="btn-secondary inline-flex items-center gap-2">
            ← Zurück zu allen Immobilien
          </Link>
        </div>
      </section>
    </div>
  );
}
